import { useParams, Navigate } from "react-router-dom";
import { AlertTriangle, Target, Route } from "lucide-react";
import Seo, { faqSchema, breadcrumbSchema } from "@/components/Seo";
import PageHero from "@/components/shared/PageHero";
import SectionHeading from "@/components/shared/SectionHeading";
import FaqAccordion from "@/components/shared/FaqAccordion";
import CtaSection from "@/components/shared/CtaSection";
import CtaButton from "@/components/shared/CtaButton";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { getIndustry } from "@/data/industries";

const SECTION = "max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28";
const name = (t) => t.replace("GEO for ", "");

const DEF = {
  challenges: ["Buyers increasingly research through AI before contacting you.", "Inconsistent data confuses how AI describes your brand.", "Competitors are being cited while you remain invisible."],
  opportunities: ["Become the brand AI recommends in your category.", "Structure your information for accurate AI citation.", "Capture high-intent enquiries from AI-driven research."],
  strategy: ["Entity and knowledge-graph optimization.", "Structured data and schema implementation.", "Answer-first content engineering.", "Authority and citation building."],
  faqs: [{ q: "How does GEO apply to my industry?", a: "We tailor entity, structured data, content and authority work to how AI engines describe and recommend brands in your specific sector." }],
};

export default function IndustryDetail() {
  const { slug } = useParams();
  const ind = getIndustry(slug);
  if (!ind) return <Navigate to="/industries" replace />;

  const challenges = ind.challenges || DEF.challenges;
  const opportunities = ind.opportunities || DEF.opportunities;
  const strategy = ind.strategy || DEF.strategy;
  const faqs = ind.faqs || DEF.faqs;

  return (
    <>
      <Seo
        title={`${ind.title} — AI Search Optimization for ${name(ind.title)}`}
        description={`${ind.short} CompuBrain AI delivers Generative Engine Optimization tailored to the ${name(ind.title)} industry.`}
        path={`/industries/${slug}`}
        schemas={[faqSchema(faqs), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Industries", path: "/industries" }, { name: ind.title, path: `/industries/${slug}` }])]}
      />
      <PageHero
        eyebrow="Industry GEO"
        title={ind.title}
        sub={ind.short}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Industries", path: "/industries" }, { name: name(ind.title) }]}
      >
        <CtaButton variant="accent" source={`industry-${slug}`} service={ind.title} title={`Request ${name(ind.title)} GEO audit`} testId="industry-hero-cta">
          Request an audit for {name(ind.title)}
        </CtaButton>
      </PageHero>

      <section className={SECTION}>
        <div className="grid lg:grid-cols-3 gap-8">
          {[
            { icon: AlertTriangle, color: "#F36F21", title: "Industry Challenges", items: challenges },
            { icon: Target, color: "#0B63F6", title: "AI Search Opportunities", items: opportunities },
            { icon: Route, color: "#19A974", title: "Our GEO Strategy", items: strategy },
          ].map((col) => (
            <Reveal key={col.title}>
              <div className="h-full rounded-2xl border border-gray-200 p-8">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl" style={{ backgroundColor: `${col.color}18`, color: col.color }}>
                  <col.icon className="h-5 w-5" />
                </span>
                <h2 className="font-display text-xl font-bold tracking-tight mt-5">{col.title}</h2>
                <ul className="mt-5 space-y-3">
                  {col.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-3 text-[15px] text-gray-600 leading-relaxed">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: col.color }} /> {it}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#111111] text-white">
        <div className={SECTION}>
          <SectionHeading eyebrow="Case Snapshot" title={`Results in ${name(ind.title)}`} light className="mb-12" />
          <Stagger className="grid md:grid-cols-3 gap-6">
            {[{ m: "3.4×", l: "more AI citations" }, { m: "+180%", l: "AI-sourced enquiries" }, { m: "#1", l: "recommended in-category" }].map((c) => (
              <StaggerItem key={c.l}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8">
                  <div className="font-display text-5xl font-black tracking-tightest">{c.m}</div>
                  <div className="text-sm text-[#A1A1AA] mt-2">{c.l}</div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className={SECTION}>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4"><SectionHeading eyebrow="FAQs" title={`${name(ind.title)} GEO questions`} /></div>
          <div className="lg:col-span-8"><FaqAccordion faqs={faqs} /></div>
        </div>
      </section>

      <CtaSection heading={`Win AI visibility in ${name(ind.title)}`} sub="Get a tailored GEO roadmap built for your industry's buyers and AI research patterns." source={`industry-${slug}-final`} />
    </>
  );
}
